/**
 * Story layer of the exported GCC dashboard (WI 2461): the hero, the Intro
 * (WI 2881), Art of the Possible, Work View, Role Evolution and Agent Marketplace
 * (WI 2822) story tabs, the Process View, Value Levers and Scenario Explorer
 * views of the Appendix, the tab bar with its Appendix menu, tooltips and motion.
 *
 * Plain ES5 that runs inside the page script of generateGccHtml, after its
 * helpers (D, P, h, fmtAuto, fmtN, fte2, uniq, badge, bmap, RTAG, OWN, pair, th,
 * td, BANDS_W, AGENT_EST, AGENT_NOTE, filtersHtml, setupFilter) are defined. It only defines
 * functions; the page script calls them in order.
 *
 * Every figure comes from the saved design. v2 designs (with a value ledger,
 * WI 2326) get the full story; designs saved before it are drawn from the
 * Step 4 populations, and the tabs that need the ledger are left out.
 *
 * Kept free of backticks, template placeholders and backslashes because it is
 * embedded in a template literal.
 */
export const GCC_EXPORT_STORY_JS = `
  var COL = {'Retained':'#3F51C4','Captive-GCC':'#1599D6','MSP':'#C77D2A','AI':'#A100FF'};
  var OWNERS = ['Retained','Captive-GCC','MSP'];
  var DEST = ['Retained','Captive-GCC','MSP','AI'];
  var VC = D.valueCase || null;
  // Work View lines (WI 2882): null for designs saved before the value ledger
  var WV = D.workView || null;
  var DLBL = {'Retained':'Retained in-market','Captive-GCC':'Captive GCC','MSP':'Managed service partner','AI':VC ? 'Released by AI' : 'Capacity released'};
  var INK = '#262A57';
  var MOTION = !!(window.matchMedia && window.requestAnimationFrame &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function num(n) { return +n || 0; }
  function sum(a, f) { return (a||[]).reduce(function(s,x){ return s + num(f(x)); }, 0); }
  function pct(a, b) { return b ? a / b * 100 : 0; }
  function zeroO() { return {'Retained':0,'Captive-GCC':0,'MSP':0}; }
  function fFte(n) {
    n = num(n); var a = Math.abs(n);
    if (a >= 100) return Math.round(n).toLocaleString('en-US');
    if (a >= 10) return String(Math.round(n * 10) / 10);
    return String(Math.round(n * 100) / 100);
  }
  function fUsd(n) {
    n = num(n); var a = Math.abs(n), s = n < 0 ? '−' : '';
    if (a >= 1e9) return s + '$' + (a / 1e9).toFixed(1) + 'B';
    if (a >= 1e6) return s + '$' + (a / 1e6).toFixed(a >= 1e8 ? 0 : 1) + 'M';
    if (a >= 1e3) return s + '$' + Math.round(a / 1e3) + 'K';
    return s + '$' + Math.round(a);
  }
  function fPct(n) { n = num(n); var a = Math.abs(n); return (n < 0 ? '−' : '') + (a > 0 && a < 10 ? a.toFixed(1) : String(Math.round(a))) + '%'; }
  var FMT = {fte:fFte, usd:fUsd, pct:fPct, int:function(n){ return Math.round(num(n)).toLocaleString('en-US'); }};
  /** A figure that counts up when its panel comes into view. */
  function cu(v, fmt, pre) { return '<span data-cu="' + num(v) + '" data-fmt="' + fmt + '" data-pre="' + h(pre||'') + '">' + h(pre||'') + FMT[fmt](v) + '</span>'; }
  /** Hover / focus tooltip; the first line is the heading. */
  function tip(lines) { return ' data-tip="' + h(lines.join('|')) + '" tabindex="0"'; }
  function d(i) { return ' style="--d:' + (i * 0.07).toFixed(2) + 's"'; }
  function cut(s, n) { s = String(s == null ? '' : s); return s.length > n ? s.slice(0, n - 1) + '…' : s; }
  function ownerOfTier(t) { return t === 'MSP' ? 'MSP' : (t === 'GCC-Offshore' || t === 'GCC-Nearshore') ? 'Captive-GCC' : 'Retained'; }
  function niceMax(v) {
    if (!(v > 0)) return 1;
    var p = Math.pow(10, Math.floor(Math.log(v) / Math.LN10)), m = v / p;
    return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p;
  }
  function allRoles() {
    return (D.gccPopulation && D.gccPopulation.endStateRoles || [])
      .concat(D.retainedPopulation && D.retainedPopulation.endStateRoles || []);
  }

  /** Workforce after wave k from the value ledger: AI lands in a line's transform wave, the move in its migration wave. */
  function stateAfter(lines, k) {
    var s = {fte:0, o:zeroO(), ai:0};
    lines.forEach(function(l) {
      var transformed = num(l.aiReleasedFte) > 0 && (l.transformWave || 3) <= k;
      var fte = transformed ? num(l.residualFte) : num(l.baselineFte);
      if (transformed) s.ai += num(l.aiReleasedFte);
      var moves = l.ownership && l.ownership !== 'Retained' && (l.migrationWave || 3) <= k;
      s.o[moves ? l.ownership : 'Retained'] += fte;
      s.fte += fte;
    });
    return s;
  }

  /** Today, the end state and (v2) the state after Wave 2, in one shape for every view. */
  var M = (function() {
    var comb = D.combined || {};
    var reg = D.taskRegister && D.taskRegister.taskRegister || [];
    // The Simple export carries only the register's counts (WI 2822)
    var m = D.scope ? {procs:num(D.scope.procs), tasks:num(D.scope.tasks), mid:null}
      : {procs:reg.length, tasks:sum(reg, function(p){ return (p.tasks||[]).length; }), mid:null};
    if (VC) {
      var t = VC.totals || {}, fo = t.futureFteByOwnership || {};
      m.today = {fte:num(t.currentFte), o:{'Retained':num(t.currentFte),'Captive-GCC':0,'MSP':0}, ai:0, cost:num(t.currentCostUSD)};
      m.end = {fte:num(t.residualFte), o:{'Retained':num(fo['Retained']),'Captive-GCC':num(fo['Captive-GCC']),'MSP':num(fo['MSP'])}, ai:num(t.aiReleasedFte), cost:num(t.endStateCostUSD)};
      m.saving = {total:num(t.totalSavingUSD), ai:num(t.aiSavingUSD), loc:num(t.locationSavingUSD)};
      var w2 = (VC.waves || []).filter(function(w){ return w.wave === 2; })[0];
      if ((VC.tasks || []).length && w2) {
        m.mid = stateAfter(VC.tasks, 2);
        m.mid.cost = m.today.cost - num(w2.cumulativeSavingUSD);
      }
    } else {
      var roles = allRoles();
      var today = num(comb.totalHeadcountToday) || sum(roles, function(r){ return r.headcountToday; });
      var endT = num(comb.totalHeadcountEndState) || sum(roles, function(r){ return r.headcountEndState; });
      var o = zeroO();
      roles.forEach(function(r){ o[ownerOfTier(r.sourcingTier)] += num(r.headcountEndState); });
      var os = o['Retained'] + o['Captive-GCC'] + o['MSP'];
      if (os > 0) OWNERS.forEach(function(k){ o[k] = o[k] * endT / os; }); else o['Retained'] = endT;
      m.today = {fte:today, o:{'Retained':today,'Captive-GCC':0,'MSP':0}, ai:0, cost:num(comb.totalLabourCostTodayUSD)};
      m.end = {fte:endT, o:o, ai:Math.max(0, today - endT), cost:num(comb.totalLabourCostEndStateUSD)};
      m.saving = {total:num(comb.labourSavingUSD) || (m.today.cost - m.end.cost), ai:comb.aiSavingUSD, loc:comb.locationSavingUSD};
    }
    m.costPct = pct(m.saving.total, m.today.cost);
    return m;
  })();

  // ── Hero ──────────────────────────────────────────────
  function renderHero() {
    var gen = new Date().toLocaleDateString('en-GB', {day:'numeric', month:'long', year:'numeric'});
    var chips = [['Client', P.client || '—'], ['Industry', P.industry || '—'], ['Scope', M.procs + ' processes · ' + M.tasks + ' tasks'], ['Generated', gen]];
    var head = M.today.cost > 0
      ? '<div class="hh-num">' + cu(M.costPct, 'pct', '−') + '</div>' +
        '<div class="hh-lbl">lower annual labour cost in the end state</div>' +
        '<div class="hh-sub">' + h(fUsd(M.today.cost)) + ' today → ' + h(fUsd(M.end.cost)) + ' run-rate</div>'
      : '<div class="hh-num">' + cu(M.end.ai, 'fte') + '</div><div class="hh-lbl">FTE of capacity released</div>';
    document.getElementById('gcc-hero').innerHTML =
      '<div class="hero-grid"></div>' +
      '<div class="hero-top"><div class="wordmark"><b>Global Capability Center</b><span>Design Engine</span></div>' +
      '<div class="hero-meta">' + chips.map(function(c){ return '<div class="chip"><small>' + h(c[0]) + '</small><strong>' + h(c[1]) + '</strong></div>'; }).join('') + '</div></div>' +
      '<div class="hero-body"><div id="hero-copy"></div>' +
      '<div class="hero-headline">' + head + '<div class="tagline">Higher performance<br>Bolder capabilities<br>Greater value</div></div></div>';
  }
  function setHeroCopy(t) {
    var el = document.getElementById('hero-copy');
    if (!el) return;
    el.innerHTML = '<div class="hero-swap"><div class="hero-eyebrow">' + h(P.client || 'Global Capability Center design') + ' · ' + h(t.label) + '</div>' +
      '<h1 class="hero-title">' + h(t.title) + '</h1><p class="hero-sub">' + h(t.sub) + '</p></div>';
  }

  // ── Art of the Possible ───────────────────────────────
  function donut(state, base, centre) {
    var vals = OWNERS.map(function(k){ return {k:k, v:state.o[k]}; }).concat([{k:'AI', v:state.ai}]);
    var tot = sum(vals, function(v){ return v.v; }), R = 54, C = 2 * Math.PI * R, off = 0;
    var shown = vals.filter(function(v){ return v.v > 0; }).length, gap = shown > 1 ? 2 : 0;
    var s = '<svg class="donut" viewBox="0 0 136 136" role="img" aria-label="' + h(centre + ' workforce: ' + vals.map(function(v){ return DLBL[v.k] + ' ' + fFte(v.v); }).join(', ')) + '">' +
      '<circle class="track" cx="68" cy="68" r="54"/>';
    vals.forEach(function(v, i) {
      if (!(v.v > 0) || !tot) return;
      var len = v.v / tot * C, L = Math.max(0.5, len - gap);
      s += '<circle class="seg" cx="68" cy="68" r="54" stroke="' + COL[v.k] + '" stroke-dasharray="' + L.toFixed(2) + ' ' + (C - L).toFixed(2) + '"' +
        ' stroke-dashoffset="' + (-off).toFixed(2) + '" transform="rotate(-90 68 68)" style="--d:' + (0.2 + i * 0.12).toFixed(2) + 's"' +
        tip([DLBL[v.k], fFte(v.v) + ' FTE · ' + fPct(pct(v.v, tot)) + ' of today’s capacity']) + '/>';
      off += len;
    });
    s += '<text class="c-num" x="68" y="70" text-anchor="middle">' + h(fFte(state.fte)) + '</text>' +
      '<text class="c-lbl" x="68" y="88" text-anchor="middle">Human FTE</text></svg>';
    var leg = '<ul class="legend">' + vals.filter(function(v){ return v.v > 0 || v.k === 'Retained'; }).map(function(v) {
      return '<li><span class="sw" style="background:' + COL[v.k] + '"></span><span>' + h(DLBL[v.k]) + '</span>' +
        '<span class="lv">' + h(fFte(v.v)) + '</span><span class="lp">' + h(fPct(pct(v.v, tot))) + '</span></li>';
    }).join('') + '</ul>';
    return '<div class="st-chart">' + s + leg + '</div>';
  }
  function delta(v, b, money) {
    if (!b) return '<div class="d flat">&nbsp;</div>';
    var p = pct(v - b, b);
    if (Math.abs(p) < 0.05) return '<div class="d flat">No change</div>';
    var cls = money ? (p < 0 ? '' : 'up') : 'flat';
    return '<div class="d ' + cls + '">' + (p < 0 ? '↓ ' : '↑ ') + h(fPct(Math.abs(p))) + ' vs today</div>';
  }
  function stateCard(name, when, s, base, color, i) {
    return '<div class="state rv lift" style="--st:' + color + ';--d:' + (i * 0.12).toFixed(2) + 's">' +
      '<div class="st-name">' + h(name) + '</div><div class="st-when">' + h(when) + '</div>' +
      '<div class="st-stats">' +
        '<div class="stat"><div class="v">' + cu(s.fte, 'fte') + '</div><div class="l">Human FTE</div>' + (base ? delta(s.fte, base.fte, false) : '<div class="d flat">Baseline</div>') + '</div>' +
        '<div class="stat"><div class="v">' + cu(s.ai, 'fte') + '</div><div class="l">' + h(DLBL['AI']) + ' (FTE)</div><div class="d flat">' + (base ? h(fPct(pct(s.ai, base.fte))) + ' of today' : '&nbsp;') + '</div></div>' +
        '<div class="stat"><div class="v">' + cu(s.cost, 'usd') + '</div><div class="l">Annual labour cost</div>' + (base ? delta(s.cost, base.cost, true) : '<div class="d flat">Run-rate</div>') + '</div>' +
      '</div>' + donut(s, base, name) + '</div>';
  }
  function unlockItems() {
    var items = [];
    var tagged = D.taggedTasks && D.taggedTasks.taggedTasks || [];
    if (M.end.ai > 0 && M.today.fte > 0)
      items.push([fPct(pct(M.end.ai, M.today.fte)), VC ? 'of today’s work released by AI' : 'of today’s headcount released', fFte(M.end.ai) + ' FTE of capacity']);
    var moved = M.end.o['Captive-GCC'] + M.end.o['MSP'];
    if (moved > 0)
      items.push([fFte(M.end.o['Captive-GCC']), 'FTE delivered from a captive GCC', M.end.o['MSP'] > 0 ? '+ ' + fFte(M.end.o['MSP']) + ' FTE with a managed service partner' : fPct(pct(moved, M.end.fte)) + ' of the end-state workforce']);
    if (VC) {
      var caps = VC.capabilities || [];
      var shifting = caps.filter(function(c){ var f = c.futureFteByOwnership || {}; return num(f['Captive-GCC']) + num(f['MSP']) > 0; }).length;
      if (caps.length) items.push([shifting + ' of ' + caps.length, 'capabilities move work to a GCC or partner', shifting < caps.length ? (caps.length - shifting) + ' stay fully in-market' : 'each with its own ownership × location split']);
      var w1 = (VC.waves || []).filter(function(w){ return w.wave === 1; })[0];
      if (w1 && num(w1.cumulativeSavingUSD) > 0) items.push([fUsd(w1.cumulativeSavingUSD), 'run-rate saving from Wave 1', 'starting in the first 6 months']);
    } else if (M.saving.total > 0) {
      items.push([fUsd(M.saving.total), 'annual labour saving', fPct(M.costPct) + ' of today’s labour cost']);
    }
    if (tagged.length) {
      var ai = tagged.filter(function(t){ return t.disposition === 'Eliminate' || t.disposition === 'Automate' || t.disposition === 'Agentify'; }).length;
      items.push([ai + ' of ' + tagged.length, 'tasks eliminated, automated or run by agents', tagged.filter(function(t){ return t.disposition === 'Augment'; }).length + ' more augmented by AI']);
    }
    var roles = allRoles();
    var evolving = roles.filter(function(r){ return r.tag === 'Evolved' || r.tag === 'New'; }).length;
    if (roles.length && evolving) items.push([evolving + ' of ' + roles.length, 'end-state roles evolve or are new', 'with new skills to build']);
    return items.slice(0, 5);
  }
  /** True when two workforce states show the same figures, to the precision the cards print. */
  function sameState(a, b) {
    function close(x, y, tol) { return Math.abs(num(x) - num(y)) < tol; }
    return close(a.fte, b.fte, 0.005) && close(a.ai, b.ai, 0.005) && close(a.cost, b.cost, 0.5) &&
      OWNERS.every(function(k){ return close(a.o[k], b.o[k], 0.005); });
  }
  function renderOverview() {
    var states = [stateCard('Today', 'Baseline · in-market workforce', M.today, null, INK, 0)];
    var tomorrowWhen = 'After Waves 1–2 · ' + BANDS_W[1].split('–')[0] + '–' + BANDS_W[2].split('–')[1];
    // When nothing lands in Wave 3, the end state is Tomorrow: show one Tomorrow card, not two identical ones (WI 2822)
    if (M.mid && sameState(M.mid, M.end)) {
      states.push(stateCard('Tomorrow', tomorrowWhen, M.end, M.today, COL['AI'], 1));
    } else {
      if (M.mid) states.push(stateCard('Tomorrow', tomorrowWhen, M.mid, M.today, COL['Captive-GCC'], 1));
      states.push(stateCard('Future', VC ? 'After Wave 3 · AI-enabled model with a scaled GCC' : 'End-state operating model', M.end, M.today, COL['AI'], M.mid ? 2 : 1));
    }
    var unlock = unlockItems();
    document.getElementById('pnl-overview').innerHTML =
      '<section class="sec"><div class="states">' + states.join('') + '</div></section>' +
      (unlock.length ? '<section class="sec rv"' + d(3) + '><div class="sec-head"><div><div class="sec-title">Value unlocked</div><div class="sec-sub">What this design delivers, calculated from the task-level baseline.</div></div></div>' +
        '<div class="unlock">' + unlock.map(function(u) {
          return '<div><div class="u-num">' + h(u[0]) + '</div><div class="u-lbl">' + h(u[1]) + '</div><div class="u-sub">' + h(u[2]) + '</div></div>';
        }).join('') + '</div></section>' : '') +
      '<section class="sec rv"' + d(4) + '><div class="sec-head"><div><div class="sec-title">Where the value sits</div><div class="sec-sub">Headcount and run-rate labour cost by population.</div></div></div>' +
        '<div class="vc-cards" id="gcc-vc"></div></section>';
  }

  // ── Value Levers ──────────────────────────────────────
  function yAxis(W, pl, pr, pt, plotH, max, fmt) {
    var s = '';
    for (var i = 0; i <= 4; i++) {
      var v = max * i / 4, y = pt + plotH * (1 - i / 4);
      s += '<line class="' + (i ? 'grid' : 'ax') + '" x1="' + pl + '" x2="' + (W - pr) + '" y1="' + y.toFixed(1) + '" y2="' + y.toFixed(1) + '"/>' +
        '<text class="tick" x="' + (pl - 8) + '" y="' + (y + 4).toFixed(1) + '" text-anchor="end">' + h(fmt(v)) + '</text>';
    }
    return s;
  }
  /** Cost bridge: totals stand on the axis; steps hang from the running level, split by lever. */
  function waterfall(steps) {
    var W = 600, H = 320, pl = 58, pr = 10, pt = 34, pb = 50, plotH = H - pt - pb;
    var max = niceMax(steps[0].v), n = steps.length, slot = (W - pl - pr) / n, bw = Math.min(64, slot * 0.56);
    var y = function(v){ return pt + plotH * (1 - v / max); };
    var s = '<svg class="chart" viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="Run-rate labour cost bridge">' + yAxis(W, pl, pr, pt, plotH, max, fUsd);
    var level = steps[0].v, prevX = null;
    steps.forEach(function(st, i) {
      var x = pl + slot * i + (slot - bw) / 2;
      if (prevX !== null) s += '<line class="conn fade" x1="' + prevX + '" x2="' + x.toFixed(1) + '" y1="' + y(level).toFixed(1) + '" y2="' + y(level).toFixed(1) + '"' + d(i + 2) + '/>';
      if (st.total) {
        s += '<rect class="bar" x="' + x.toFixed(1) + '" y="' + y(st.v).toFixed(1) + '" width="' + bw.toFixed(1) + '" height="' + Math.max(0, plotH - (y(st.v) - pt)).toFixed(1) + '" rx="4" fill="' + st.c + '"' + d(i) + tip([st.lbl, fUsd(st.v) + ' annual labour cost']) + '/>' +
          '<text class="val fade" x="' + (x + bw / 2).toFixed(1) + '" y="' + (y(st.v) - 8).toFixed(1) + '" text-anchor="middle"' + d(i + 3) + '>' + h(fUsd(st.v)) + '</text>';
        level = st.v;
      } else {
        var top = level, tot = sum(st.parts, function(p){ return p.v; });
        st.parts.forEach(function(p, j) {
          if (!(p.v > 0)) return;
          var y0 = y(top), y1 = y(top - p.v);
          s += '<rect class="bar bar-down" x="' + x.toFixed(1) + '" y="' + y0.toFixed(1) + '" width="' + bw.toFixed(1) + '" height="' + Math.max(1, y1 - y0 - (j < st.parts.length - 1 ? 1 : 0)).toFixed(1) + '" rx="3" fill="' + p.c + '"' + d(i + j * 0.5) + tip([st.lbl + ' · ' + p.l, '−' + fUsd(p.v) + ' run-rate']) + '/>';
          top -= p.v;
        });
        s += '<text class="val-s fade" x="' + (x + bw / 2).toFixed(1) + '" y="' + (y(level) - 8).toFixed(1) + '" text-anchor="middle"' + d(i + 3) + '>' + (tot > 0 ? '−' + h(fUsd(tot)) : '—') + '</text>';
        level = top;
      }
      s += '<text class="xl" x="' + (x + bw / 2).toFixed(1) + '" y="' + (H - pb + 20) + '" text-anchor="middle">' + h(st.lbl) + '</text>' +
        (st.sub ? '<text class="xs" x="' + (x + bw / 2).toFixed(1) + '" y="' + (H - pb + 36) + '" text-anchor="middle">' + h(st.sub) + '</text>' : '');
      prevX = (x + bw).toFixed(1);
    });
    return s + '</svg>';
  }
  /** Grouped bars on one FTE axis. groups: [{lbl, sub, bars:[{v,c,l}]}] */
  function groupedBars(groups, aria) {
    var W = 600, H = 320, pl = 46, pr = 10, pt = 30, pb = 50, plotH = H - pt - pb;
    var max = niceMax(Math.max.apply(null, groups.map(function(g){ return Math.max.apply(null, g.bars.map(function(b){ return num(b.v); }).concat([0])); }).concat([0])));
    var slot = (W - pl - pr) / groups.length, k = groups[0].bars.length, bw = Math.min(30, slot * 0.5 / k);
    var s = '<svg class="chart" viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' + h(aria) + '">' + yAxis(W, pl, pr, pt, plotH, max, fFte);
    groups.forEach(function(g, i) {
      var x0 = pl + slot * i + (slot - (bw * k + 2 * (k - 1))) / 2;
      g.bars.forEach(function(b, j) {
        var v = num(b.v), x = x0 + j * (bw + 2), hh = plotH * v / max;
        s += '<rect class="bar" x="' + x.toFixed(1) + '" y="' + (pt + plotH - hh).toFixed(1) + '" width="' + bw.toFixed(1) + '" height="' + Math.max(v > 0 ? 1 : 0, hh).toFixed(1) + '" rx="4" fill="' + b.c + '"' + d(i * 2 + j) + tip([g.lbl + ' · ' + b.l, fFte(v) + ' FTE']) + '/>' +
          (v > 0 ? '<text class="val-s fade" x="' + (x + bw / 2).toFixed(1) + '" y="' + (pt + plotH - hh - 7).toFixed(1) + '" text-anchor="middle"' + d(i * 2 + j + 3) + '>' + h(fFte(v)) + '</text>' : '');
      });
      s += '<text class="xl" x="' + (pl + slot * i + slot / 2).toFixed(1) + '" y="' + (H - pb + 20) + '" text-anchor="middle">' + h(g.lbl) + '</text>' +
        (g.sub ? '<text class="xs" x="' + (pl + slot * i + slot / 2).toFixed(1) + '" y="' + (H - pb + 36) + '" text-anchor="middle">' + h(g.sub) + '</text>' : '');
    });
    return s + '</svg>';
  }
  function legendRow(keys) {
    return '<div class="legend-row">' + keys.map(function(k){ return '<span><i style="background:' + k[1] + '"></i>' + h(k[0]) + '</span>'; }).join('') + '</div>';
  }
  function chartCard(title, sub, legend, body, callout, i) {
    return '<div class="card rv"' + d(i) + '><div class="sec-head"><div><div class="card-title">' + h(title) + '</div><div class="card-sub">' + h(sub) + '</div></div>' + (callout || '') + '</div>' +
      (legend ? legend : '') + '<div style="margin-top:12px">' + body + '</div></div>';
  }
  function renderLevers() {
    var el = document.getElementById('pnl-levers');
    if (!M.today.cost && !M.today.fte) { el.innerHTML = '<div class="card empty">No value case figures in this design.</div>'; return; }
    var callout = M.today.cost > 0 ? '<div class="callout"><b>' + cu(M.costPct, 'pct', '−') + '</b><span>lower annual cost</span></div>' : '';
    if (!VC) {
      var g = D.gccPopulation || {}, r = D.retainedPopulation || {};
      var wf = waterfall([
        {total:true, v:M.today.cost, c:INK, lbl:'Today'},
        {lbl:'GCC-eligible', sub:'saving', parts:[{v:num(g.labourSavingUSD), c:COL['Captive-GCC'], l:'GCC-eligible saving'}]},
        {lbl:'Retained', sub:'saving', parts:[{v:num(r.labourSavingUSD), c:COL['Retained'], l:'Retained saving'}]},
        {total:true, v:M.end.cost, c:COL['AI'], lbl:'End state'},
      ]);
      var hc = groupedBars([
        {lbl:'GCC-eligible', bars:[{v:g.totalHeadcountToday, c:'#B8BCD4', l:'Today'}, {v:g.totalHeadcountEndState, c:COL['Captive-GCC'], l:'End state'}]},
        {lbl:'Retained', bars:[{v:r.totalHeadcountToday, c:'#B8BCD4', l:'Today'}, {v:r.totalHeadcountEndState, c:COL['Retained'], l:'End state'}]},
      ], 'Headcount today and in the end state by population');
      el.innerHTML = '<div class="grid2">' +
        chartCard('Run-rate cost bridge', 'Annual labour cost, today to end state, by population', legendRow([['GCC-eligible saving', COL['Captive-GCC']], ['Retained saving', COL['Retained']]]), wf, callout, 0) +
        chartCard('Headcount by population', 'FTE today and in the end state', legendRow([['Today', '#B8BCD4'], ['End state', COL['AI']]]), hc, '', 1) + '</div>';
      return;
    }
    var waves = VC.waves || [], flow = D.sequencing && D.sequencing.waveFlow || [], lines = VC.tasks || [];
    function wv(arr, w) { return arr.filter(function(x){ return x.wave === w; })[0] || {}; }
    var NAMES = {1:'Quick wins', 2:'Build and scale', 3:'Transform the operating model'};
    var LC = {1:COL['AI'], 2:COL['Captive-GCC'], 3:COL['Retained']};
    var levers = [1, 2, 3].map(function(w, i) {
      var vw = wv(waves, w), fw = wv(flow, w), byCap = {};
      lines.forEach(function(l) {
        var v = ((l.transformWave || 3) === w ? num(l.aiSavingUSD) : 0) + ((l.migrationWave || 3) === w ? num(l.locationSavingUSD) : 0);
        if (v > 0) byCap[l.capabilityName] = (byCap[l.capabilityName] || 0) + v;
      });
      var top = Object.keys(byCap).sort(function(a, b){ return byCap[b] - byCap[a]; }).slice(0, 3);
      return '<div class="lever rv" style="--lc:' + LC[w] + ';--d:' + (i * 0.1).toFixed(2) + 's">' +
        '<div class="lv-k">Wave ' + w + ' · ' + h(BANDS_W[w]) + '</div><div class="lv-t">' + h(NAMES[w]) + '</div>' +
        '<div class="lv-figs">' +
          '<div><div class="v">' + cu(vw.aiSavingUSD, 'usd') + '</div><div class="l">AI saving starts</div></div>' +
          '<div><div class="v">' + cu(vw.locationSavingUSD, 'usd') + '</div><div class="l">Location saving starts</div></div>' +
          '<div><div class="v">' + cu(fw.transformFte, 'fte') + '</div><div class="l">FTE released by AI</div></div>' +
          '<div><div class="v">' + cu(fw.migrationFte, 'fte') + '</div><div class="l">FTE moved</div></div>' +
        '</div>' +
        (top.length ? '<ul class="lv-caps">' + top.map(function(c){ return '<li><span>' + h(c) + '</span><b>' + h(fUsd(byCap[c])) + '</b></li>'; }).join('') + '</ul>'
          : '<div class="note" style="border-top:1px solid var(--line2);padding-top:10px">No savings start in this wave.</div>') +
        '</div>';
    });
    var steps = [{total:true, v:M.today.cost, c:INK, lbl:'Today'}].concat([1, 2, 3].map(function(w) {
      var vw = wv(waves, w);
      return {lbl:'Wave ' + w, sub:BANDS_W[w], parts:[{v:num(vw.aiSavingUSD), c:COL['AI'], l:'AI productivity'}, {v:num(vw.locationSavingUSD), c:COL['Captive-GCC'], l:'Location arbitrage'}]};
    })).concat([{total:true, v:M.end.cost, c:INK, lbl:'End state'}]);
    var cap = groupedBars([1, 2, 3].map(function(w) {
      var fw = wv(flow, w);
      return {lbl:'Wave ' + w, sub:BANDS_W[w], bars:[{v:fw.transformFte, c:COL['AI'], l:'Released by AI'}, {v:fw.migrationFte, c:COL['Captive-GCC'], l:'Moved to GCC / partner'}]};
    }), 'FTE released by AI and moved, by wave');
    var byFn = {};
    (VC.capabilities || []).forEach(function(c) {
      var f = byFn[c.functionScope] || (byFn[c.functionScope] = {ai:0, loc:0, cur:0});
      f.ai += num(c.aiSavingUSD); f.loc += num(c.locationSavingUSD); f.cur += num(c.currentCostUSD);
    });
    var fns = Object.keys(byFn).sort(function(a, b){ return (byFn[b].ai + byFn[b].loc) - (byFn[a].ai + byFn[a].loc); });
    var fmax = Math.max.apply(null, fns.map(function(f){ return byFn[f].ai + byFn[f].loc; }).concat([1]));
    var fnBars = '<div class="fn-bars">' + fns.map(function(f, i) {
      var x = byFn[f];
      return '<div class="fnb"><span class="n" title="' + h(f) + '">' + h(f) + '</span><span class="track">' +
        (x.ai > 0 ? '<span class="hbar" style="width:' + (x.ai / fmax * 100).toFixed(2) + '%;background:' + COL['AI'] + ';--d:' + (i * 0.05).toFixed(2) + 's"' + tip([f + ' · AI productivity', fUsd(x.ai)]) + '></span>' : '') +
        (x.loc > 0 ? '<span class="hbar" style="width:' + (x.loc / fmax * 100).toFixed(2) + '%;background:' + COL['Captive-GCC'] + ';--d:' + (i * 0.05 + 0.1).toFixed(2) + 's"' + tip([f + ' · Location arbitrage', fUsd(x.loc)]) + '></span>' : '') +
        '</span><span class="t">' + h(fUsd(x.ai + x.loc)) + '</span></div>';
    }).join('') + '</div>';
    el.innerHTML =
      '<section class="sec"><div class="levers">' + levers.join('') + '</div></section>' +
      '<section class="sec grid2">' +
        chartCard('Run-rate cost bridge', 'Annual labour cost, today to end state, by the wave each saving starts in', legendRow([['AI productivity', COL['AI']], ['Location arbitrage', COL['Captive-GCC']]]), waterfall(steps), callout, 3) +
        chartCard('Capacity released and moved', 'FTE released by AI and FTE moved to a GCC or partner, per wave', legendRow([['Released by AI', COL['AI']], ['Moved to GCC / partner', COL['Captive-GCC']]]), cap,
          '<div class="callout"><b>' + cu(M.end.ai, 'fte') + '</b><span>FTE released in total</span></div>', 4) +
      '</section>' +
      (fns.length ? '<section class="sec">' + chartCard('Savings by function', 'Run-rate saving per function, split by lever', legendRow([['AI productivity', COL['AI']], ['Location arbitrage', COL['Captive-GCC']]]), fnBars, '', 5) + '</section>' : '');
  }

  // ── Flow chart (WI 2884) ──────────────────────────────
  /**
   * A Sankey drawn as inline SVG. left: [{i, name, v}], right: [{j, name, v, c, sub}],
   * links: [{l, r, v}] with l and r the nodes' i and j. Each node is a focusable group
   * (data-l / data-r) whose tooltip lists its links with their FTE.
   */
  function sankey(left, right, links, aria) {
    var W = 860, LW = 230, RW = 230, NW = 10, gapL = 6, gapR = 10;
    var H = Math.max(320, Math.max(left.length, right.length) * 28);
    var lt = sum(left, function(n){ return n.v; }), rt = sum(right, function(n){ return n.v; });
    var sc = (H - Math.max(gapL * (left.length - 1), gapR * (right.length - 1), 0)) / Math.max(lt, rt);
    var xL = LW, xR = W - RW - NW, mx = (xL + NW + xR) / 2;
    function stack(nodes, gap) {
      var y = (H - (sum(nodes, function(n){ return n.v; }) * sc + gap * (nodes.length - 1))) / 2;
      return nodes.map(function(n){ var p = {y:y, h:n.v * sc}; y += p.h + gap; return p; });
    }
    var LY = stack(left, gapL), RY = stack(right, gapR), lp = {}, rp = {};
    left.forEach(function(n, k){ lp[n.i] = k; });
    right.forEach(function(n, k){ rp[n.j] = k; });
    links = links.filter(function(k){ return k.l in lp && k.r in rp && k.v > 0; }).map(function(k){ return {l:k.l, r:k.r, v:k.v, w:k.v * sc}; });
    // Links leave a left node in the order of their right nodes, and enter a right node in the order of their left nodes
    var off = {};
    links.slice().sort(function(a, b){ return lp[a.l] - lp[b.l] || rp[a.r] - rp[b.r]; })
      .forEach(function(k){ var o = off['l' + k.l] || 0; k.y0 = LY[lp[k.l]].y + o + k.w / 2; off['l' + k.l] = o + k.w; });
    links.slice().sort(function(a, b){ return rp[a.r] - rp[b.r] || lp[a.l] - lp[b.l]; })
      .forEach(function(k){ var o = off['r' + k.r] || 0; k.y1 = RY[rp[k.r]].y + o + k.w / 2; off['r' + k.r] = o + k.w; });
    var ln = {}, rn = {};
    left.forEach(function(n){ ln[n.i] = n; });
    right.forEach(function(n){ rn[n.j] = n; });
    function linkTips(side, id) {
      return links.filter(function(k){ return k[side] === id; }).sort(function(a, b){ return b.v - a.v; })
        .map(function(k){ return (side === 'l' ? '→ ' + rn[k.r].name : '← ' + ln[k.l].name) + ': ' + fFte(k.v) + ' FTE'; });
    }
    var paths = links.map(function(k, n) {
      return '<path class="lnk" data-l="' + k.l + '" data-r="' + k.r + '" d="M' + (xL + NW) + ' ' + k.y0.toFixed(1) + 'C' + mx + ' ' + k.y0.toFixed(1) + ' ' + mx + ' ' + k.y1.toFixed(1) + ' ' + xR + ' ' + k.y1.toFixed(1) + '"' +
        ' stroke="' + rn[k.r].c + '" stroke-width="' + Math.max(1, k.w).toFixed(1) + '" style="--d:' + (0.15 + Math.min(n, 20) * 0.03).toFixed(2) + 's"' +
        ' data-tip="' + h([ln[k.l].name + ' → ' + rn[k.r].name, fFte(k.v) + ' FTE'].join('|')) + '"/>';
    }).join('');
    function node(attr, id, n, p, x, label) {
      var lines = [n.name + (n.sub ? ' · ' + n.sub : ''), fFte(n.v) + ' FTE'].concat(linkTips(attr, id));
      return '<g class="nd" data-' + attr + '="' + id + '" role="button" aria-label="' + h(n.name + ', ' + fFte(n.v) + ' FTE' + (n.sub ? ', ' + n.sub : '')) + '"' + tip(lines) + '>' +
        '<rect class="node" x="' + x + '" y="' + p.y.toFixed(1) + '" width="' + NW + '" height="' + Math.max(1, p.h).toFixed(1) + '" rx="2" fill="' + (n.c || INK) + '"/>' + label + '</g>';
    }
    var nodes = left.map(function(n, k) {
      var p = LY[k];
      return node('l', n.i, n, p, xL, '<text x="' + (xL - 10) + '" y="' + (p.y + p.h / 2 + 4).toFixed(1) + '" text-anchor="end"><tspan class="nl">' + h(cut(n.name, 28)) + '</tspan><tspan class="nv" dx="8">' + h(fFte(n.v)) + '</tspan></text>');
    }).join('') + right.map(function(n, k) {
      var p = RY[k];
      return node('r', n.j, n, p, xR, '<text x="' + (xR + NW + 10) + '" y="' + (p.y + p.h / 2 - 2).toFixed(1) + '"><tspan class="nl">' + h(cut(n.name, 28)) + '</tspan></text>' +
        '<text class="nv" x="' + (xR + NW + 10) + '" y="' + (p.y + p.h / 2 + 12).toFixed(1) + '">' + h(fFte(n.v) + ' FTE' + (n.sub ? ' · ' + n.sub : '')) + '</text>');
    }).join('');
    return '<svg class="chart sankey" viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' + h(aria) + '">' + paths + nodes + '</svg>';
  }

  // ── Intro (WI 2881) ───────────────────────────────────
  var INTRO_PURPOSE = 'How AI and a Global Capability Center change ' + (P.client ? P.client + '’s' : 'the organisation’s') +
    ' work, workforce and labour cost, from today’s baseline to the end state.';
  /** The 5-step GCC method, one line per step, the same in both downloads. */
  var METHOD = [
    ['Current-State Baseline', 'Today’s processes and tasks, and the roles that do them with their FTE.'],
    ['AI Disposition & Residual Workload', 'Each task tagged Eliminate, Agentify, Automate, Augment or Human, and the work left for people calculated.'],
    ['Residual Sourcing: Ownership × Location', 'The work left for people placed with a captive GCC, a managed service partner or the retained organisation, and by location.'],
    ['Sequencing: Shift & Transform', 'When each change lands: AI transformation and migration, phased over three waves.'],
    ['Future Workforce & Value Case', VC
      ? 'The end-state workforce and the run-rate value case, with AI and location savings kept separate.'
      : 'The end-state headcount and labour cost savings of the GCC-eligible and retained populations.'],
  ];
  var METHOD_COLORS = ['#3F51C4','#1599D6','#0F7B4F','#C77D2A','#A100FF'];
  /** One headline figure per story tab, from the saved design: [value, label]. */
  function introFigure(id) {
    if (id === 'overview') return M.today.cost > 0 ? ['−' + fPct(M.costPct), 'lower annual labour cost'] : [fFte(M.end.ai), 'FTE of capacity released'];
    if (id === 'workview') return WV ? [WV.totals.functions + ' · ' + WV.totals.tasks, 'functions · tasks in scope'] : [M.procs + ' · ' + M.tasks, 'processes · tasks in scope'];
    if (id === 'roleevo') { var n = evolvingRoles().length; return [String(n), n === 1 ? 'role changes shape' : 'roles change shape']; }
    if (id === 'agents') { var b = (D.agentBuilds || []).filter(function(x){ return BUILD[x.disposition]; }).length; return [String(b), b === 1 ? 'AI build' : 'AI builds']; }
    return null;
  }
  /** The Intro tab: who and what the design is for, a card per story tab that opens it, and the method. */
  function renderIntro(tabs, go) {
    var el = document.getElementById('pnl-intro');
    var cards = [];
    tabs.forEach(function(t, i) { if (t.story && t.intro) cards.push({t:t, i:i, n:cards.length + 2}); });
    el.innerHTML =
      '<section class="sec"><div class="card intro-head rv"><div class="intro-k">Global Capability Center design</div>' +
        '<h2 class="intro-client">' + h(P.client || 'Global Capability Center design') + '</h2><p class="intro-purpose">' + h(INTRO_PURPOSE) + '</p></div></section>' +
      '<section class="sec rv"' + d(1) + '><div class="sec-head"><div><div class="sec-title">The story in four views</div><div class="sec-sub">Each view opens from its card. The detail behind them is in the Appendix.</div></div></div>' +
        '<ul class="intro-cards card-list" aria-label="Story tabs">' + cards.map(function(c) {
          var f = introFigure(c.t.id);
          return '<li><button class="intro-card lift" type="button" data-go="' + c.i + '">' +
            '<span class="ic-n">0' + c.n + '</span><span class="ic-t">' + h(c.t.label) + '</span><span class="ic-d">' + h(c.t.intro) + '</span>' +
            (f ? '<span class="ic-f"><b>' + h(f[0]) + '</b> ' + h(f[1]) + '</span>' : '') + '</button></li>';
        }).join('') + '</ul></section>' +
      '<section class="sec rv"' + d(2) + '><div class="card"><div class="ov-title">How this design was built: the GCC method in 5 steps</div>' +
        '<div class="ov-sub">Each step feeds the next. Every figure in this dashboard is calculated from the saved design.</div>' +
        '<ol class="step-list">' + METHOD.map(function(s, i) {
          return '<li class="step-item"><div class="step-circle" style="--sc:' + METHOD_COLORS[i] + '">' + i + '</div>' +
            '<div class="step-body"><div class="step-title">' + h(s[0]) + '</div><div class="step-desc">' + h(s[1]) + '</div></div></li>';
        }).join('') + '</ol></div></section>';
    el.querySelectorAll('[data-go]').forEach(function(btn) {
      btn.addEventListener('click', function(){ go(+btn.getAttribute('data-go'), true); });
    });
  }

  /** A row of headline figures: [[value, label, sub], ...] */
  function strip(items) {
    return '<section class="sec rv"><div class="unlock">' + items.map(function(u) {
      return '<div><div class="u-num">' + h(u[0]) + '</div><div class="u-lbl">' + h(u[1]) + '</div><div class="u-sub">' + h(u[2]) + '</div></div>';
    }).join('') + '</div></section>';
  }
  function countBy(items, key) {
    var c = {};
    items.forEach(function(x){ c[x[key]] = (c[x[key]] || 0) + 1; });
    return c;
  }

  // ── Role Evolution (WI 2822) ──────────────────────────
  var EVO_TAGS = ['Evolved', 'New', 'Consolidated'];
  /** Every end-state role that changes shape, largest first, with the population it belongs to. */
  function evolvingRoles() {
    function seg(pop, name) { return (pop && pop.endStateRoles || []).map(function(r){ return {r:r, seg:name}; }); }
    return seg(D.gccPopulation, 'GCC-eligible').concat(seg(D.retainedPopulation, 'Retained'))
      .filter(function(x){ return EVO_TAGS.indexOf(x.r.tag) >= 0; })
      .sort(function(a, b){ return num(b.r.headcountToday) - num(a.r.headcountToday) || String(a.r.roleName).localeCompare(String(b.r.roleName)); });
  }
  var RE = D.roleEvolution || null;
  var RE_TAGS = ['Retained', 'Evolved', 'Consolidated', 'New'];
  /** Unique skills of the end-state roles, in order. */
  function skillsOf(roles, key) {
    var out = [];
    roles.forEach(function(r){ (r[key] || []).forEach(function(s){ if (out.indexOf(s) < 0) out.push(s); }); });
    return out;
  }
  function chips(a, cls) { return a.length ? '<div class="rev-chips' + (cls ? ' ' + cls : '') + '">' + a.map(function(s){ return '<span>' + h(s) + '</span>'; }).join('') + '</div>' : '<div class="note">—</div>'; }
  /** The three skill lists of a role card. */
  function skillBlocks(roles) {
    return '<div class="evo-skills">' +
      '<div><div class="rev-k">Skills moving away</div>' + chips(skillsOf(roles, 'skillsOut')) + '</div>' +
      '<div><div class="rev-k">Skills coming in</div>' + chips(skillsOf(roles, 'skillsIn')) + '</div>' +
      '<div><div class="rev-k">Net-new skills</div>' + chips(skillsOf(roles, 'skillsNetNew'), 'new') + '</div></div>';
  }
  /**
   * The Role Evolution tab (WI 2884): a Sankey of today's roles to the end-state roles and
   * the capacity AI releases (or, for designs without source roles, to where the work
   * goes), with segment, tag and function filters and a card for the chosen role.
   */
  function renderRoleEvolution() {
    var el = document.getElementById('pnl-roleevo');
    if (!RE || !(RE.flows || []).length) { el.innerHTML = '<div class="card empty">This design has no end-state roles to show how today’s roles evolve.</div>'; return; }
    var T = RE.totals, src = RE.mode === 'sourceRoles', evolving = evolvingRoles();
    var active = {seg:'', tag:'', fn:''}, sel = null;
    var ex = D.workView && D.workView.excluded || {};
    var groups = [
      {key:'seg', label:'Segment', vals:['GCC-eligible', 'Retained'].filter(function(s){ return RE.flows.some(function(f){ return f.seg === s; }); })},
      {key:'tag', label:'Tag', vals:RE_TAGS.filter(function(t){ return RE.flows.some(function(f){ return f.tag === t; }); })},
    ];
    if (RE.functions.length) groups.push({key:'fn', label:'Function', vals:RE.functions});
    var nRoles = RE.targets.filter(function(t){ return t.kind === 'role'; }).length;
    el.innerHTML =
      strip([
        [fFte(T.today), 'FTE today', plural(RE.today.length, 'role', 'roles') + ' in today’s baseline'],
        [fFte(T.endState), 'end-state FTE', src ? plural(nRoles, 'end-state role', 'end-state roles') : 'retained, in a captive GCC or with a partner'],
        [fFte(T.ai), 'FTE released by AI', fPct(pct(T.ai, T.today)) + ' of today’s FTE'],
        [String(evolving.length), evolving.length === 1 ? 'role changes shape' : 'roles change shape', 'evolved, new or consolidated'],
      ]) +
      '<section class="sec">' + filtersHtml('evo', groups) +
        '<div class="card evo-chart-card"><div class="sankey-head"><span>Today’s roles</span><span>' + (src ? 'End-state roles and capacity released' : 'Where the work goes') + '</span></div>' +
        '<div id="evo-chart"></div><p class="note evo-tot" id="evo-tot"></p>' +
        (src ? '' : '<p class="note evo-fb">This design was saved before Step 4 named the source roles of each end-state role, so today’s roles are shown going to where their work ends up.</p>') +
        (num(ex.tasks) > 0 ? '<p class="note evo-excl">Tasks excluded in the task register review are included here, as in Art of the Possible: the left side is the whole baseline.</p>' : '') +
        '</div>' +
        '<div class="evo-card" id="evo-card" aria-live="polite"><div class="card note evo-hint">Choose a role on either side of the chart to see its card.</div></div>' +
      '</section>';
    var chart = document.getElementById('evo-chart'), tot = document.getElementById('evo-tot'), card = document.getElementById('evo-card');
    function shown(f) { return (!active.seg || f.seg === active.seg) && (!active.tag || f.tag === active.tag) && (!active.fn || f.fn === active.fn); }
    function colour(t) { return t.kind === 'ai' ? COL['AI'] : COL[t.owner]; }
    function sub(t) { return t.kind === 'role' ? RE.roles[t.role].sourcingTier : ''; }
    function draw() {
      var all = !active.seg && !active.tag && !active.fn, lv = {}, rv = {}, lk = {};
      RE.flows.filter(shown).forEach(function(f) {
        lv[f.from] = (lv[f.from] || 0) + f.fte; rv[f.to] = (rv[f.to] || 0) + f.fte;
        lk[f.from + '|' + f.to] = (lk[f.from + '|' + f.to] || 0) + f.fte;
      });
      // Unfiltered, the nodes show the design's own figures; filtered, the sum of the links kept
      var left = RE.today.map(function(t, i){ return {i:i, name:t.name, v:all ? t.fte : lv[i] || 0}; }).filter(function(n){ return n.v > 0.001; });
      var right = RE.targets.map(function(t, j){ return {j:j, name:t.name, v:all ? t.fte : rv[j] || 0, c:colour(t), sub:sub(t), ai:t.kind === 'ai'}; }).filter(function(n){ return n.v > 0.001; });
      var links = Object.keys(lk).map(function(k){ var p = k.split('|'); return {l:+p[0], r:+p[1], v:lk[k]}; });
      chart.innerHTML = left.length ? sankey(left, right, links, src ? 'How today’s roles become the end-state roles' : 'Where the work of today’s roles goes') : '<div class="empty">No roles in this selection.</div>';
      var lt = sum(left, function(n){ return n.v; }), at = sum(right.filter(function(n){ return n.ai; }), function(n){ return n.v; }), et = sum(right, function(n){ return n.v; }) - at;
      tot.textContent = 'Left: ' + fFte(lt) + ' FTE today. Right: ' + fFte(et) + ' FTE in the end state + ' + fFte(at) + ' FTE released by AI = ' + fFte(et + at) + ' FTE.';
      if (sel && !chart.querySelector('.nd[data-' + sel[0] + '="' + sel[1] + '"]')) { sel = null; card.innerHTML = '<div class="card note evo-hint">Choose a role on either side of the chart to see its card.</div>'; }
      mark(sel);
    }
    /** Highlights a node's links (a = [side, id]), or clears the highlight. */
    function mark(a) {
      var svg = chart.querySelector('svg');
      if (!svg) return;
      svg.querySelectorAll('.hl').forEach(function(x){ x.classList.remove('hl'); });
      svg.querySelectorAll('.nd.sel').forEach(function(x){ x.classList.remove('sel'); });
      svg.classList.toggle('dim', !!a);
      if (!a) return;
      svg.querySelectorAll('[data-' + a[0] + '="' + a[1] + '"]').forEach(function(x){ x.classList.add('hl'); });
      if (sel) { var s = svg.querySelector('.nd[data-' + sel[0] + '="' + sel[1] + '"]'); if (s) s.classList.add('sel'); }
    }
    function nodeOf(t) { return t && t.closest ? t.closest('.nd') : null; }
    function sideOf(n) { return n.hasAttribute('data-l') ? ['l', n.getAttribute('data-l')] : ['r', n.getAttribute('data-r')]; }
    /** The links of a node, all filters applied, as [name, FTE] largest first. */
    function linksOf(a) {
      var by = {};
      RE.flows.filter(shown).forEach(function(f) {
        if (String(a[0] === 'l' ? f.from : f.to) !== a[1]) return;
        var t = RE.targets[f.to];
        var k = a[0] === 'l' ? t.name + (t.kind === 'role' ? ' · ' + sub(t) : '') : RE.today[f.from].name;
        by[k] = (by[k] || 0) + f.fte;
      });
      return Object.keys(by).sort(function(x, y){ return by[y] - by[x]; }).map(function(k){ return [k, by[k]]; });
    }
    function showCard(a) {
      var head, hc, badgeHtml = '', title, roles = [];
      if (a[0] === 'l') {
        var t = RE.today[+a[1]];
        head = t.name; hc = fFte(t.fte) + ' FTE today';
        title = src ? 'Becomes' : 'Its work goes to';
        roles = t.roles.map(function(i){ return RE.roles[i]; });
      } else {
        var g = RE.targets[+a[1]];
        head = g.name; title = 'Draws from';
        if (g.kind === 'role') {
          var r = RE.roles[g.role];
          hc = fFte(r.headcountToday) + ' → ' + fFte(r.headcountEndState) + ' FTE · ' + r.segment + ' · ' + r.sourcingTier;
          badgeHtml = bmap(RTAG, r.tag);
          roles = [r];
        } else hc = fFte(g.fte) + ' FTE' + (g.kind === 'ai' ? ' of today’s work released by AI' : ' in the end state');
      }
      var ls = linksOf(a);
      card.innerHTML = '<div class="card rev evo-role"><div class="rev-top"><div><div class="rev-name">' + h(head) + '</div><div class="rev-hc">' + h(hc) + '</div></div>' + badgeHtml + '</div>' +
        '<div><div class="rev-k">' + h(title) + '</div><ul class="evo-links">' + ls.map(function(x){ return '<li><span>' + h(x[0]) + '</span><b>' + h(fFte(x[1])) + ' FTE</b></li>'; }).join('') + '</ul></div>' +
        (roles.length ? skillBlocks(roles) : '') + '</div>';
    }
    function choose(n) { sel = sideOf(n); mark(sel); showCard(sel); }
    chart.addEventListener('mouseover', function(e){ var n = nodeOf(e.target); mark(n ? sideOf(n) : sel); });
    chart.addEventListener('mouseleave', function(){ mark(sel); });
    chart.addEventListener('focusin', function(e){ var n = nodeOf(e.target); if (n) mark(sideOf(n)); });
    chart.addEventListener('focusout', function(){ mark(sel); });
    chart.addEventListener('click', function(e){ var n = nodeOf(e.target); if (n) choose(n); });
    chart.addEventListener('keydown', function(e) {
      var n = nodeOf(e.target);
      if (n && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); choose(n); }
    });
    document.getElementById('f-evo').addEventListener('click', function(e) {
      var b = e.target;
      if (!b.classList.contains('pill')) return;
      active[b.getAttribute('data-key')] = b.getAttribute('data-val');
      this.querySelectorAll('[data-key="' + b.getAttribute('data-key') + '"]').forEach(function(p){ p.classList.remove('active'); });
      b.classList.add('active');
      draw();
      if (sel) showCard(sel);
    });
    draw();
  }

  // ── Agent Marketplace (WI 2822): the GCC take on TN's Automation & AI Builds Catalog ──
  var BUILD = {
    'Agentify':  {lbl:'Agentic AI',         cls:'b-purple', c:'#A100FF'},
    'Automate':  {lbl:'Automation',         cls:'b-blue',   c:'#3F51C4'},
    'Eliminate': {lbl:'Eliminated with AI', cls:'b-red',    c:'#C0392B'},
    'Augment':   {lbl:'AI-augmented',       cls:'b-yellow', c:'#C77D2A'}
  };
  var BUILD_ORDER = ['Agentify', 'Automate', 'Eliminate', 'Augment'];
  function renderAgentMarketplace() {
    var el = document.getElementById('pnl-agents');
    var builds = (D.agentBuilds || []).filter(function(b){ return BUILD[b.disposition]; });
    if (!builds.length) { el.innerHTML = '<div class="card empty">No AI builds in this design: all of its work stays with people.</div>'; return; }
    var flat = builds.map(function(b){ return {type:BUILD[b.disposition].lbl, agent:b.agentMatch ? 'Yes' : 'No'}; });
    var types = BUILD_ORDER.filter(function(k){ return builds.some(function(b){ return b.disposition === k; }); }).map(function(k){ return BUILD[k].lbl; });
    var withFte = builds.filter(function(b){ return b.releasedFte != null; });
    var agents = builds.filter(function(b){ return b.agentMatch; }).length;
    var head = [[String(builds.length), builds.length === 1 ? 'AI build' : 'AI builds', 'tasks AI takes on or assists']];
    if (withFte.length) head.push([fFte(sum(withFte, function(b){ return b.releasedFte; })), 'FTE of capacity released', 'across these builds']);
    head.push([agents + ' of ' + builds.length, 'likely have an agent', AGENT_EST]);
    el.innerHTML = strip(head) +
      '<section class="sec">' +
      filtersHtml('agt', [
        {key:'type', label:'Build type', vals:types, counts:countBy(flat, 'type'), total:flat.length},
        {key:'agent', label:AGENT_EST, vals:['Yes', 'No'], counts:countBy(flat, 'agent'), total:flat.length},
      ]) +
      '<ul class="build-grid card-list" aria-label="Automation and AI builds">' + builds.map(function(b) {
        var t = BUILD[b.disposition];
        return '<li class="build lift" data-card data-type="' + h(t.lbl) + '" data-agent="' + (b.agentMatch ? 'Yes' : 'No') + '" style="--bc:' + t.c + '">' +
          '<div class="build-top">' + badge(t.lbl, t.cls) + (b.agentMatch ? badge(AGENT_EST, 'b-green') : '') + '</div>' +
          '<div class="build-name">' + h(b.taskName) + '</div>' +
          (b.capabilityName ? '<div class="build-cap">' + h(b.capabilityName) + (b.functionScope ? ' · ' + h(b.functionScope) : '') + '</div>' : '') +
          '<div class="build-figs">' +
            (b.releasedFte != null ? '<div><div class="v">' + h(fFte(b.releasedFte)) + '</div><div class="l">FTE released</div></div>' : '') +
            (b.wave ? '<div><div class="v">Wave ' + h(b.wave) + '</div><div class="l">' + h(BANDS_W[b.wave] || '') + '</div></div>' : '') +
          '</div></li>';
      }).join('') + '</ul>' +
      '<p class="note" style="margin-top:12px">' + h(AGENT_NOTE) + '</p></section>';
    setupFilter('agt', 'pnl-agents', ['type', 'agent']);
  }

  // ── Work View (WI 2882): functions, then capabilities, then roles and tasks, through one lens ──
  var WV_OWN = {'Retained':'Retained', 'Captive-GCC':'Captive GCC', 'MSP':'MSP'};
  var WV_DISP = ['Eliminate', 'Automate', 'Agentify', 'Augment', 'Human'];
  var WV_DISP_C = {'Eliminate':'#C0392B', 'Automate':'#3F51C4', 'Agentify':'#A100FF', 'Augment':'#C77D2A', 'Human':'#9AA0B8'};
  var WV_DISP_B = {'Eliminate':'b-red', 'Automate':'b-blue', 'Agentify':'b-purple', 'Augment':'b-yellow', 'Human':'b-gray'};
  var WV_LOCS = ['HQ', 'Markets', 'Nearshore', 'Offshore'];
  /** One hue per owner, one shade per location (HQ darkest). */
  var WV_SHADES = {'Retained':['#3F51C4','#6E7BD3','#9DA6E2','#C9CEF0'], 'Captive-GCC':['#0B6E9C','#1599D6','#5BB8E3','#A3D8F0'], 'MSP':['#8F5A1E','#C77D2A','#DDA766','#EFD3AE']};
  var WV_WAVE_C = {'1':'#A100FF', '2':'#1599D6', '3':'#3F51C4', '0':'#C9CCD8'};
  var WV_OWN_KEYS = [];
  OWNERS.forEach(function(o){ WV_LOCS.forEach(function(l){ WV_OWN_KEYS.push(o + '|' + l); }); });
  /** A task's first wave of change: the earlier of its AI transformation and migration waves; 0 when neither applies. */
  function firstWave(t) {
    var w = [t.transformWave, t.migrationWave].filter(function(x){ return x; });
    return w.length ? Math.min.apply(null, w) : 0;
  }
  var LENSES = {
    disp: {lbl:'AI disposition', keys:WV_DISP, of:function(t){ return t.disposition; },
      col:function(k){ return WV_DISP_C[k] || '#9AA0B8'; }, name:function(k){ return k; }},
    own: {lbl:'Ownership × location', keys:WV_OWN_KEYS, of:function(t){ return t.ownership + '|' + t.location; },
      col:function(k){ var p = k.split('|'); return (WV_SHADES[p[0]] || WV_SHADES['Retained'])[Math.max(0, WV_LOCS.indexOf(p[1]))]; },
      name:function(k){ var p = k.split('|'); return pair(p[0], p[1]); }},
    wave: {lbl:'Wave', keys:['1', '2', '3', '0'], of:function(t){ return String(firstWave(t)); },
      col:function(k){ return WV_WAVE_C[k]; }, name:function(k){ return k === '0' ? 'No change' : 'Wave ' + k + ' · ' + BANDS_W[k]; }}
  };
  var WV_LENS_ORDER = ['disp', 'own', 'wave'];
  /** The lens values present in the tasks, in the lens's order (unknown values last). */
  function lensKeys(L, tasks) {
    var seen = uniq(tasks.map(L.of));
    return L.keys.filter(function(k){ return seen.indexOf(k) >= 0; }).concat(seen.filter(function(k){ return L.keys.indexOf(k) < 0; }));
  }
  /** Tasks grouped by a field, in the order each value first appears. */
  function wvGroup(tasks, key) {
    var out = [], idx = Object.create(null);
    tasks.forEach(function(t) {
      var k = t[key];
      if (!(k in idx)) { idx[k] = out.length; out.push({name:k, tasks:[]}); }
      out[idx[k]].tasks.push(t);
    });
    return out;
  }
  function wvFigs(tasks) {
    return {fte:sum(tasks, function(t){ return t.baselineFte; }), ai:sum(tasks, function(t){ return t.aiReleasedFte; }), res:sum(tasks, function(t){ return t.residualFte; })};
  }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
  /** Today's FTE of the tasks, split by the lens: what each card's work becomes. */
  function wvBar(tasks, L) {
    var by = {}, tot = 0;
    tasks.forEach(function(t){ var k = L.of(t), v = num(t.baselineFte); by[k] = (by[k] || 0) + v; tot += v; });
    if (!(tot > 0)) return '<span class="wv-bar"></span>';
    return '<span class="wv-bar" aria-hidden="true">' + lensKeys(L, tasks).filter(function(k){ return by[k] > 0; }).map(function(k) {
      return '<span data-k="' + h(k) + '" style="width:' + (by[k] / tot * 100).toFixed(2) + '%;background:' + L.col(k) + '" title="' + h(L.name(k) + ': ' + fFte(by[k]) + ' FTE today') + '"></span>';
    }).join('') + '</span>';
  }
  function wvCard(g, i, kind, sub, L) {
    var f = wvFigs(g.tasks);
    return '<li><button class="wv-card lift" type="button" data-wv-' + kind + '="' + i + '">' +
      '<span class="wv-name">' + h(g.name) + '</span><span class="wv-sub">' + h(sub) + '</span>' +
      '<span class="wv-figs"><span><b>' + h(fFte(f.fte)) + '</b> FTE today</span><span><b>' + h(fFte(f.ai)) + '</b> released by AI</span>' +
      '<span><b>' + h(fFte(f.res)) + '</b> end state</span></span>' + wvBar(g.tasks, L) + '</button></li>';
  }
  function wvTask(t, L) {
    var k = L.of(t);
    var waves = 'T ' + (t.transformWave ? 'W' + t.transformWave : '—') + ' / M ' + (t.migrationWave ? 'W' + t.migrationWave : '—');
    return '<li class="wv-task" data-lens-key="' + h(k) + '" style="--wc:' + L.col(k) + '">' +
      '<span class="wv-dot" title="' + h(L.name(k)) + '"></span>' +
      '<span class="wv-tn">' + h(t.taskName) + '<span class="wv-sr"> (' + h(L.lbl + ': ' + L.name(k)) + ')</span></span>' +
      '<span class="wv-tf">' + h(fFte(t.baselineFte)) + ' → ' + h(fFte(t.residualFte)) + ' FTE</span>' +
      '<span class="wv-tb">' + badge(t.disposition, WV_DISP_B[t.disposition]) + badge(pair(t.ownership, t.location), OWN[t.ownership]) + badge(waves, 'b-gray') + '</span></li>';
  }
  function renderWorkView() {
    var el = document.getElementById('pnl-workview');
    if (!WV) {
      el.innerHTML = '<div class="card empty wv-none">The Work View is drawn from the task-level value ledger, which this design was saved without. Run the design again to see how its work changes.</div>';
      return;
    }
    var tasks = WV.tasks || [];
    if (!tasks.length) {
      el.innerHTML = '<div class="card empty wv-none">Every task of this design was excluded in the task register review, so there is no work to show.</div>';
      return;
    }
    var t = WV.totals || {}, fo = t.futureFteByOwnership || {}, ex = WV.excluded || {};
    var owners = OWNERS.filter(function(o){ return num(fo[o]) > 0; }).map(function(o){ return WV_OWN[o] + ' ' + fFte(fo[o]); }).join(' · ') || 'No work left for people';
    var st = {fn:null, cap:null, lens:'disp'};
    var fns = wvGroup(tasks, 'functionScope'), caps = [];
    el.innerHTML =
      '<div class="wv-metrics">' + strip([
        [String(t.functions), num(t.functions) === 1 ? 'function' : 'functions', 'in scope'],
        [String(t.capabilities), num(t.capabilities) === 1 ? 'capability' : 'capabilities', 'processes in today’s baseline'],
        [String(t.tasks), num(t.tasks) === 1 ? 'task' : 'tasks', 'with their FTE today'],
        [fFte(t.currentFte), 'FTE today', 'the baseline workforce'],
        [fFte(t.aiReleasedFte), 'FTE released by AI', fPct(pct(num(t.aiReleasedFte), num(t.currentFte))) + ' of today’s work'],
        [fFte(t.residualFte), 'end-state FTE', owners],
      ]) + '</div>' +
      (num(ex.tasks) > 0 ? '<p class="note wv-excl">' + h(plural(num(ex.tasks), 'task', 'tasks') + ' (' + fFte(ex.fte) + ' FTE today) excluded in the task register review ' + (num(ex.tasks) === 1 ? 'is' : 'are') +
        ' left out of this view. Art of the Possible still includes ' + (num(ex.tasks) === 1 ? 'it' : 'them') + ': exclusions do not recalculate the design.') + '</p>' : '') +
      '<section class="sec"><div id="wv-body"></div></section>';
    var body = document.getElementById('wv-body');
    function crumbs() {
      var parts = [{lbl:'All functions', up:0}];
      if (st.fn !== null) parts.push({lbl:fns[st.fn].name, up:1});
      if (st.cap !== null) parts.push({lbl:caps[st.cap].name, up:2});
      return '<nav class="wv-crumbs" aria-label="Work View level"><ol>' + parts.map(function(p, i) {
        return '<li>' + (i === parts.length - 1
          ? '<span class="wv-here" aria-current="page" tabindex="-1">' + h(p.lbl) + '</span>'
          : '<button type="button" data-wv-up="' + p.up + '">' + h(p.lbl) + '</button>') + '</li>';
      }).join('') + '</ol></nav>';
    }
    function draw(focus) {
      var L = LENSES[st.lens], scope = st.cap !== null ? caps[st.cap].tasks : st.fn !== null ? fns[st.fn].tasks : tasks;
      var head, list;
      if (st.cap !== null) {
        head = ['Roles and tasks', 'Each task’s FTE today → end state, its AI disposition, ownership × location and waves (T: AI transformation, M: migration).'];
        list = '<ul class="wv-roles card-list" aria-label="Roles">' + wvGroup(scope, 'roleTitle').map(function(r) {
          var f = wvFigs(r.tasks);
          return '<li class="card wv-role"><div class="wv-role-h"><span class="wv-name">' + h(r.name) + '</span>' +
            '<span class="wv-sub">' + h(plural(r.tasks.length, 'task', 'tasks') + ' · ' + fFte(f.fte) + ' → ' + fFte(f.res) + ' FTE') + '</span></div>' +
            '<ul class="wv-tasks card-list">' + r.tasks.map(function(x){ return wvTask(x, L); }).join('') + '</ul></li>';
        }).join('') + '</ul>';
      } else if (st.fn !== null) {
        head = ['Capabilities', 'Choose a capability to see its roles and tasks.'];
        list = '<ul class="wv-grid card-list" aria-label="Capabilities">' + caps.map(function(c, i) {
          return wvCard(c, i, 'cap', plural(uniq(c.tasks.map(function(x){ return x.roleTitle; })).length, 'role', 'roles') + ' · ' + plural(c.tasks.length, 'task', 'tasks'), L);
        }).join('') + '</ul>';
      } else {
        head = ['Functions', 'Choose a function to see its capabilities.'];
        list = '<ul class="wv-grid card-list" aria-label="Functions">' + fns.map(function(g, i) {
          return wvCard(g, i, 'fn', plural(uniq(g.tasks.map(function(x){ return x.capabilityName; })).length, 'capability', 'capabilities') + ' · ' + plural(g.tasks.length, 'task', 'tasks'), L);
        }).join('') + '</ul>';
      }
      body.innerHTML = crumbs() +
        '<div class="filters wv-lens" role="group" aria-label="Colour tasks by"><span class="f-lbl">Colour by:</span>' + WV_LENS_ORDER.map(function(k) {
          return '<button class="pill' + (k === st.lens ? ' active' : '') + '" type="button" data-wv-lens="' + k + '" aria-pressed="' + (k === st.lens) + '">' + h(LENSES[k].lbl) + '</button>';
        }).join('') + '</div>' +
        legendRow(lensKeys(L, scope).map(function(k){ return [L.name(k), L.col(k)]; })) +
        '<div class="sec-head wv-head"><div><div class="sec-title">' + h(head[0]) + '</div><div class="sec-sub">' + h(head[1]) + '</div></div></div>' + list;
      if (focus === 'crumb') body.querySelector('.wv-here').focus();
      else if (focus === 'lens') body.querySelector('[data-wv-lens="' + st.lens + '"]').focus();
    }
    body.addEventListener('click', function(e) {
      var b = e.target.closest ? e.target.closest('[data-wv-fn],[data-wv-cap],[data-wv-up],[data-wv-lens]') : null;
      if (!b) return;
      if (b.hasAttribute('data-wv-lens')) { st.lens = b.getAttribute('data-wv-lens'); draw('lens'); return; }
      if (b.hasAttribute('data-wv-fn')) {
        st.fn = +b.getAttribute('data-wv-fn'); st.cap = null;
        caps = wvGroup(fns[st.fn].tasks, 'capabilityName');
      } else if (b.hasAttribute('data-wv-cap')) st.cap = +b.getAttribute('data-wv-cap');
      else {
        var up = +b.getAttribute('data-wv-up');
        if (up < 1) st.fn = null;
        if (up < 2) st.cap = null;
      }
      draw('crumb');
    });
    draw();
  }

  // ── Process View (WI 2885): each Step 0 process, grouped by function, task by task ──
  /** A wave cell: the wave and its band, or a dash when the task has none. */
  function pvWave(w) { return w ? 'Wave ' + w + (BANDS_W[w] ? ' · ' + BANDS_W[w] : '') : '—'; }
  /** The dispositions of some tasks, for a group's data-disp-list filter attribute. */
  function pvDisps(tasks) { return uniq(tasks.map(function(t){ return t.disposition; })).join('|'); }
  /**
   * The Work View's ledger lines (register order, S0b exclusions removed) as tables: a
   * section per function, a card per process, its tasks in order. Function and
   * disposition filters hide rows, and any process or function left with none.
   */
  function renderProcessView() {
    var el = document.getElementById('pnl-process');
    if (!WV) {
      el.innerHTML = '<div class="card empty pv-none">The Process View is drawn from the task-level value ledger, which this design was saved without. Run the design again to see each process task by task.</div>';
      return;
    }
    var tasks = WV.tasks || [];
    if (!tasks.length) {
      el.innerHTML = '<div class="card empty pv-none">Every task of this design was excluded in the task register review, so there are no processes to show.</div>';
      return;
    }
    var ex = WV.excluded || {};
    var fns = wvGroup(tasks, 'functionScope');
    var cols = ['#', 'Task', 'Role', 'Disposition', 'Residual FTE', 'Ownership × location', 'Transformation wave', 'Migration wave'];
    el.innerHTML =
      (num(ex.tasks) > 0 ? '<p class="note pv-excl">' + h(plural(num(ex.tasks), 'task', 'tasks') + ' excluded in the task register review ' + (num(ex.tasks) === 1 ? 'is' : 'are') + ' left out of this view.') + '</p>' : '') +
      filtersHtml('pv', [
        {key:'fn', label:'Function', vals:fns.map(function(g){ return g.name; }), counts:countBy(tasks, 'functionScope'), total:tasks.length},
        // Labelled as the Work View's lens, and kept apart from the label of the Full-only Disposition tab
        {key:'disp', label:'AI disposition', vals:lensKeys(LENSES.disp, tasks), counts:countBy(tasks, 'disposition'), total:tasks.length},
      ]) +
      fns.map(function(fg) {
        var procs = wvGroup(fg.tasks, 'capabilityName');
        return '<section class="sec pv-fn" data-card data-fn="' + h(fg.name) + '" data-disp-list="' + h(pvDisps(fg.tasks)) + '">' +
          '<div class="sec-head pv-head"><div><div class="sec-title">' + h(fg.name) + '</div><div class="sec-sub">' + h(plural(procs.length, 'process', 'processes') + ' · ' + plural(fg.tasks.length, 'task', 'tasks')) + '</div></div></div>' +
          procs.map(function(pg) {
            var f = wvFigs(pg.tasks);
            return '<div class="pv-proc" data-card data-fn="' + h(fg.name) + '" data-disp-list="' + h(pvDisps(pg.tasks)) + '">' +
              '<div class="pv-proc-h"><span class="wv-name">' + h(pg.name) + '</span><span class="wv-sub">' + h(plural(pg.tasks.length, 'task', 'tasks') + ' · ' + fFte(f.fte) + ' → ' + fFte(f.res) + ' FTE') + '</span></div>' +
              '<div class="tbl-wrap"><table><thead><tr>' + cols.map(function(c){ return th(c); }).join('') + '</tr></thead><tbody>' +
              pg.tasks.map(function(t, i) {
                return '<tr data-fn="' + h(fg.name) + '" data-disp="' + h(t.disposition) + '">' +
                  '<td class="pv-n">' + (i + 1) + '</td>' + td(h(t.taskName)) + td(h(t.roleTitle)) + td(badge(t.disposition, WV_DISP_B[t.disposition])) +
                  td(h(fFte(t.residualFte))) + td(badge(pair(t.ownership, t.location), OWN[t.ownership])) + td(h(pvWave(t.transformWave))) + td(h(pvWave(t.migrationWave))) + '</tr>';
              }).join('') + '</tbody></table></div></div>';
          }).join('') + '</section>';
      }).join('') +
      '<div class="card empty pv-nomatch" hidden>No tasks match these filters.</div>';
    setupFilter('pv', 'pnl-process', ['fn', 'disp']);
    // Runs after setupFilter's handler, so the groups are already shown or hidden
    document.getElementById('f-pv').addEventListener('click', function() {
      el.querySelector('.pv-nomatch').hidden = !!el.querySelector('.pv-fn:not(.hidden)');
    });
  }

  // ── Scenario Explorer (v2 only) ───────────────────────
  function scenario(a, b) {
    var o = zeroO(), ai = 0, cost = 0;
    (VC.tasks || []).forEach(function(l) {
      var rel = num(l.aiReleasedFte) * a, res = num(l.baselineFte) - rel;
      var mv = l.ownership && l.ownership !== 'Retained' ? res * b : 0;
      ai += rel; o['Retained'] += res - mv; if (mv) o[l.ownership] += mv;
      cost += num(l.currentCostUSD) - num(l.aiSavingUSD) * a - (num(l.residualFte) > 0 ? num(l.locationSavingUSD) / num(l.residualFte) * mv : 0);
    });
    return {o:o, ai:ai, fte:o['Retained'] + o['Captive-GCC'] + o['MSP'], cost:cost};
  }
  function renderScenario() {
    var el = document.getElementById('pnl-scenario');
    if (!el) return;
    function slider(id, label, hint) {
      return '<div><label for="' + id + '">' + h(label) + '</label><div class="hint">' + h(hint) + '</div>' +
        '<div class="ctl-row"><input type="range" id="' + id + '" min="0" max="100" step="5" value="100" style="--p:100%"><output id="' + id + '-o" for="' + id + '">100%</output></div></div>';
    }
    el.innerHTML = '<section class="sec"><div class="sc">' +
      '<div class="card rv"><div class="card-title">Adjust assumptions</div><div class="card-sub" style="margin-bottom:18px">100% on both equals the design</div><div class="ctl">' +
        slider('sc-ai', 'AI realisation', 'Share of the AI-released capacity, and its saving, that is realised.') +
        slider('sc-mv', 'GCC and partner move', 'Share of the residual work bound for a captive GCC or partner that actually moves.') +
        '<button class="btn-ghost" id="sc-reset" type="button">Reset to design</button>' +
        '<div class="note">Scales each task in the value ledger linearly. Annual run-rate labour cost only; one-off build and transition costs are not included.</div>' +
      '</div></div>' +
      '<div class="card rv"' + d(1) + '><div class="card-title">Workforce and cost</div><div class="card-sub" style="margin-bottom:12px">Today’s capacity by where it ends up</div>' +
        legendRow(DEST.map(function(k){ return [DLBL[k], COL[k]]; })) + '<div class="stack-cols" id="sc-cols" style="margin-top:16px"></div><div class="scol-lbls" id="sc-lbls"></div></div>' +
      '<div class="card rv ko-card"' + d(2) + '><div class="card-title" style="margin-bottom:10px">Key outcomes</div><div class="ko" id="sc-ko"></div></div>' +
      '</div></section>';
    var sa = document.getElementById('sc-ai'), sm = document.getElementById('sc-mv');
    var designState = scenario(1, 1), cap = M.today.fte;
    function col(s) {
      var parts = OWNERS.map(function(k){ return [k, s.o[k]]; }).concat([['AI', s.ai]]);
      var max = cap || 1;
      return '<div class="scol"><div class="tot">' + h(fFte(s.fte)) + ' FTE</div><div class="stk">' +
        parts.map(function(p){ return '<span style="height:' + (p[1] / max * 240).toFixed(1) + 'px;background:' + COL[p[0]] + '"' + tip([DLBL[p[0]], fFte(p[1]) + ' FTE']) + '></span>'; }).join('') + '</div></div>';
    }
    function update() {
      var a = num(sa.value) / 100, b = num(sm.value) / 100, s = scenario(a, b);
      sa.style.setProperty('--p', sa.value + '%'); sm.style.setProperty('--p', sm.value + '%');
      document.getElementById('sc-ai-o').textContent = sa.value + '%';
      document.getElementById('sc-mv-o').textContent = sm.value + '%';
      var cols = document.getElementById('sc-cols');
      if (!cols.children.length) {
        cols.innerHTML = col(M.today) + col(s) + col(designState);
        document.getElementById('sc-lbls').innerHTML = '<div>Today<small>' + h(fUsd(M.today.cost)) + '</small></div><div>Scenario<small id="sc-cost">' + h(fUsd(s.cost)) + '</small></div><div>Design end state<small>' + h(fUsd(designState.cost)) + '</small></div>';
      } else {
        var mid = cols.children[1], spans = mid.querySelectorAll('.stk span');
        var vals = OWNERS.map(function(k){ return s.o[k]; }).concat([s.ai]);
        vals.forEach(function(v, i){ spans[i].style.height = (v / (cap || 1) * 240).toFixed(1) + 'px'; spans[i].setAttribute('data-tip', [DLBL[DEST[i]], fFte(v) + ' FTE'].join('|')); });
        mid.querySelector('.tot').textContent = fFte(s.fte) + ' FTE';
        document.getElementById('sc-cost').textContent = fUsd(s.cost);
      }
      var saving = M.today.cost - s.cost;
      var ko = [
        [fPct(pct(s.fte - M.today.fte, M.today.fte)), 'Human FTE change', fFte(M.today.fte) + ' → ' + fFte(s.fte) + ' FTE'],
        [fFte(s.ai), 'FTE released by AI', fPct(pct(s.ai, M.today.fte)) + ' of today’s capacity'],
        [fUsd(saving), 'Run-rate saving', fPct(pct(saving, M.today.cost)) + ' lower annual labour cost'],
        [fFte(s.o['Captive-GCC']), 'FTE in a captive GCC', s.o['MSP'] > 0 ? '+ ' + fFte(s.o['MSP']) + ' FTE with a partner' : 'no partner work'],
      ];
      document.getElementById('sc-ko').innerHTML = ko.map(function(k){ return '<div><div class="v">' + h(k[0]) + '</div><div class="l">' + h(k[1]) + '</div><div class="s">' + h(k[2]) + '</div></div>'; }).join('');
    }
    sa.addEventListener('input', update); sm.addEventListener('input', update);
    document.getElementById('sc-reset').addEventListener('click', function(){ sa.value = 100; sm.value = 100; update(); });
    update();
  }

  // ── Motion, tooltips, tabs ────────────────────────────
  function countUp(root) {
    root.querySelectorAll('[data-cu]').forEach(function(el) {
      var end = num(el.getAttribute('data-cu')), f = FMT[el.getAttribute('data-fmt')] || FMT.int, pre = el.getAttribute('data-pre') || '', t0 = null, dur = 1200;
      function step(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur), e = 1 - Math.pow(1 - p, 4);
        el.textContent = pre + f(end * e);
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }
  /** Replays a panel's entrance: the CSS hides marks until .in is added a frame later. */
  function replay(pnl) {
    if (!pnl) return;
    if (!MOTION) { pnl.classList.add('in'); return; }
    pnl.classList.remove('in');
    void pnl.offsetWidth;
    requestAnimationFrame(function(){ requestAnimationFrame(function(){ pnl.classList.add('in'); }); });
    countUp(pnl);
  }
  function initTooltips() {
    var tt = document.createElement('div');
    tt.className = 'tt'; tt.setAttribute('role', 'tooltip');
    document.body.appendChild(tt);
    function show(el, x, y) {
      tt.innerHTML = '';
      (el.getAttribute('data-tip') || '').split('|').forEach(function(t, i) {
        var row = document.createElement('div');
        if (i === 0) { var b = document.createElement('b'); b.textContent = t; row.appendChild(b); } else row.textContent = t;
        tt.appendChild(row);
      });
      var w = tt.offsetWidth, hh = tt.offsetHeight, left = Math.max(8, Math.min(window.innerWidth - w - 8, x + 14)), top = y - hh - 14;
      if (top < 8) top = y + 18;
      tt.style.left = left + 'px'; tt.style.top = top + 'px';
      tt.classList.add('on');
    }
    function find(t) { return t && t.closest ? t.closest('[data-tip]') : null; }
    document.addEventListener('mousemove', function(e){ var el = find(e.target); if (el) show(el, e.clientX, e.clientY); else tt.classList.remove('on'); });
    document.addEventListener('focusin', function(e){ var el = find(e.target); if (!el) return; var r = el.getBoundingClientRect(); show(el, r.left + r.width / 2, r.top); });
    document.addEventListener('focusout', function(){ tt.classList.remove('on'); });
    document.addEventListener('scroll', function(){ tt.classList.remove('on'); }, true);
  }
  /**
   * Builds the tab bar and the empty panels. Story tabs (t.story) are numbered tabs in
   * one row that scrolls sideways (WI 2485): a fade and a chevron show on any side with
   * tabs out of view, and the selected tab is scrolled clear of the fade. Every other
   * view is an unnumbered item of the Appendix menu at the end of the bar (WI 2881),
   * which sits outside the scrolling row so its dropdown is never clipped.
   */
  function buildTabs(tabs) {
    var nav = document.getElementById('gcc-tabs'), wrap = document.getElementById('gcc-panels');
    var arrowL = document.getElementById('gcc-tabs-l'), arrowR = document.getElementById('gcc-tabs-r');
    var FADE = 56;
    var ind = document.createElement('span');
    ind.className = 'tab-ind';
    var btns = [];
    function updateEdges() {
      var max = nav.scrollWidth - nav.clientWidth;
      var l = nav.scrollLeft > 1, r = nav.scrollLeft < max - 1;
      nav.classList.toggle('fade-l', l);
      nav.classList.toggle('fade-r', r);
      arrowL.classList.toggle('on', l);
      arrowR.classList.toggle('on', r);
    }
    /** Scrolls the row only as far as the tab needs to clear the fades. */
    function reveal(btn) {
      var start = btn.offsetLeft, end = start + btn.offsetWidth;
      if (start - FADE < nav.scrollLeft) nav.scrollLeft = Math.max(0, start - FADE);
      else if (end + FADE > nav.scrollLeft + nav.clientWidth) nav.scrollLeft = end + FADE - nav.clientWidth;
      updateEdges();
    }
    // The story tab that keeps the tab stop while an Appendix view is open
    var lastStory = 0;
    function setTab(i, focus) {
      var inAppx = !tabs[i].story;
      if (!inAppx) lastStory = i;
      btns.forEach(function(b, j) {
        var on = i === j;
        b.classList.toggle('active', on);
        if (tabs[j].story) {
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.tabIndex = j === lastStory ? 0 : -1;
        } else b.setAttribute('aria-current', on ? 'true' : 'false');
        document.getElementById('pnl-' + tabs[j].id).classList.toggle('active', on);
      });
      appxBtn.classList.toggle('active', inAppx);
      if (inAppx) {
        ind.style.width = '0px';
        if (focus) appxBtn.focus({ preventScroll: true });
      } else {
        ind.style.left = btns[i].offsetLeft + 'px';
        ind.style.width = btns[i].offsetWidth + 'px';
        reveal(btns[i]);
        if (focus) btns[i].focus({ preventScroll: true });
      }
      setHeroCopy(tabs[i]);
      var hero = document.getElementById('gcc-hero');
      if (hero && window.scrollY > hero.offsetHeight && window.scrollTo) window.scrollTo(0, hero.offsetHeight);
      replay(document.getElementById('pnl-' + tabs[i].id));
    }
    // ── Appendix menu (WI 2881): every view that is not a story tab, unnumbered ──
    var appx = document.getElementById('gcc-appx'), appxBtn = document.getElementById('gcc-appx-btn'), menu = document.getElementById('gcc-appx-menu');
    var items = [];
    function isOpen() { return !menu.hidden; }
    function openMenu(k) {
      menu.hidden = false;
      appxBtn.setAttribute('aria-expanded', 'true');
      var cur = items.filter(function(it){ return it.classList.contains('active'); })[0];
      (k == null ? (cur || items[0]) : items[(k + items.length) % items.length]).focus();
    }
    function closeMenu(refocus) {
      if (!isOpen()) return;
      menu.hidden = true;
      appxBtn.setAttribute('aria-expanded', 'false');
      if (refocus) appxBtn.focus();
    }
    appxBtn.onclick = function(){ if (isOpen()) closeMenu(false); else openMenu(); };
    appxBtn.onkeydown = function(e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); openMenu(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); openMenu(-1); }
      else if (e.key === 'Escape') closeMenu(true);
      else if (e.key === 'ArrowLeft' && story.length) { e.preventDefault(); setTab(story[story.length - 1], true); }
    };
    menu.onkeydown = function(e) {
      var k = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); items[(k + 1) % items.length].focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); items[(k - 1 + items.length) % items.length].focus(); }
      else if (e.key === 'Home') { e.preventDefault(); items[0].focus(); }
      else if (e.key === 'End') { e.preventDefault(); items[items.length - 1].focus(); }
      else if (e.key === 'Escape') { e.preventDefault(); closeMenu(true); }
      else if (e.key === 'Tab') closeMenu(false);
      else if ((e.key === 'Enter' || e.key === ' ') && k >= 0) { e.preventDefault(); items[k].click(); }
    };
    document.addEventListener('click', function(e){ if (isOpen() && !appx.contains(e.target)) closeMenu(false); });
    var story = [];
    tabs.forEach(function(t, i) { if (t.story) story.push(i); });
    tabs.forEach(function(t, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.id = 'tab-' + t.id;
      btn.textContent = t.label;
      if (t.story) {
        btn.className = 'tab-btn story' + (i === 0 ? ' active' : '');
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-controls', 'pnl-' + t.id);
        btn.onclick = function(){ setTab(i); };
        btn.onkeydown = function(e) {
          var s = story.indexOf(i);
          if (e.key === 'ArrowRight') { e.preventDefault(); setTab(story[(s + 1) % story.length], true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); setTab(story[(s - 1 + story.length) % story.length], true); }
        };
        nav.appendChild(btn);
      } else {
        btn.className = 'appx-item';
        btn.setAttribute('role', 'menuitem');
        btn.tabIndex = -1;
        btn.onclick = function(){ closeMenu(false); setTab(i, true); };
        menu.appendChild(btn);
        items.push(btn);
      }
      btns.push(btn);
      var pnl = document.createElement('section');
      pnl.id = 'pnl-' + t.id;
      pnl.className = 'tab-pnl' + (i === 0 ? ' active' : '');
      pnl.setAttribute('role', 'tabpanel');
      pnl.setAttribute('aria-labelledby', 'tab-' + t.id);
      pnl.setAttribute('data-title', t.title);
      wrap.appendChild(pnl);
    });
    nav.appendChild(ind);
    if (!items.length) appx.hidden = true;
    arrowL.onclick = function(){ nav.scrollLeft -= nav.clientWidth * 0.6; updateEdges(); };
    arrowR.onclick = function(){ nav.scrollLeft += nav.clientWidth * 0.6; updateEdges(); };
    nav.addEventListener('scroll', updateEdges, { passive: true });
    // A plain mouse wheel scrolls vertically: over an overflowing bar, turn that into sideways scrolling
    nav.addEventListener('wheel', function(e) {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && nav.scrollWidth > nav.clientWidth) { nav.scrollLeft += e.deltaY; e.preventDefault(); }
    }, { passive: false });
    window.addEventListener('resize', function() {
      var a = btns.filter(function(b, j){ return tabs[j].story && b.classList.contains('active'); })[0];
      if (a) { ind.style.left = a.offsetLeft + 'px'; ind.style.width = a.offsetWidth + 'px'; reveal(a); }
    });
    // Show the cues from the start, and again once web fonts have settled the tab widths
    updateEdges();
    window.addEventListener('load', updateEdges);
    window.addEventListener('beforeprint', function(){ document.querySelectorAll('.tab-pnl').forEach(function(p){ p.classList.add('in'); }); });
    return setTab;
  }
  /** Runs one section so a gap in old saved data cannot blank the rest of the page. */
  function safe(fn) { try { fn(); } catch (e) { if (window.console) console.error('GCC export:', e); } }
  if (MOTION) document.documentElement.classList.add('motion');
`
